import { useTheme } from '@stores/useTheme';
import { T } from "@utils/enums";
import React, { useState, useEffect, useMemo } from 'react';
import useTimeoutVisability from "@utils/hooks/useTimeoutVisability";

interface CircleProps {
  home: number;
  away: number;
}

const Circle: React.FC<CircleProps> = ({ home, away }) => {
  const normalizedHome = useMemo(() => Math.max(0, home), [home]);
  const normalizedAway = useMemo(() => Math.max(0, away), [away]);
  const [adjustedHome, setAdjustedHome] = useState(0);
  const [adjustedAway, setAdjustedAway] = useState(0);
  const { theme } = useTheme();

  // Timeout logic for hiding component when both values are null for 5 seconds
  const bothValuesNull = home === null && away === null;
  const { isVisible } = useTimeoutVisability({
      timeout: 5000,
      dependencies: [bothValuesNull, home, away]
  });

  useEffect(() => {
    let newHome = normalizedHome;
    let newAway = normalizedAway;
    if (normalizedHome === 0 && normalizedAway === 0) {
      newHome = 1;
      newAway = 1;
    }
    const sum = newHome + newAway;
    const factor = 360 / sum;
    setAdjustedHome(Math.round(newHome * factor));
    setAdjustedAway(Math.round(newAway * factor));
  }, [normalizedHome, normalizedAway]);

  // Hide component if both values are null for 5 seconds
  if (bothValuesNull && !isVisible) {
    return null;
  }

  const size = 40
  const strokeWidth = 4
  const center = size/2
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const progressOffset2 = ((360 - adjustedHome) / 360) * circumference;
  const progressOffset3 = ((360 - adjustedAway) / 360) * circumference;

  const calculateRotationForHome = (x: number): number => {
    x = x % 360;
    const y = 180 - (x / 2);
    return Math.round(y);
  };

  const calculateRotationForAway = (x: number): number => {
    x = x % 360;
    const y = -x/2;
    return Math.round(y);
  };

  const squareSize = 4; // Rozmiar kwadratu

  const calculateSquarePosition = (angle: number) => {
    const radians = (angle - 90) * (Math.PI / 180);
    const x = center + (radius - strokeWidth / 2) * Math.cos(radians) - squareSize / 2;
    const y = center + (radius - strokeWidth / 2) * Math.sin(radians) - squareSize / 2;
    return { x, y };
  };

  const squareAngle = calculateRotationForHome(adjustedHome);

  const predictStatic = (x: number, y: number) => {
    if (normalizedHome === 0 && normalizedAway !== 0) {
      return -90
    } 
    return 0.3250 * x + -1.2144 * y + 92.9001;
  }

  const predictStatic2 = (x: number, y: number) => {
    if (normalizedAway === 0 && normalizedHome !== 0) {
      return 90
    } 
    return -0.0295 * x + -0.9880 * y + 93.5735;
  }

  return (
    <svg width={size} height={size}>
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="transparent"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={theme === T.DARK ? "#52CC8E" : "#0B6638"}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={progressOffset2}
        transform={`rotate(${calculateRotationForHome(adjustedHome)} ${center} ${center})`}
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={theme === T.DARK ? "#FFEF6E" : "#C35000"}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={progressOffset3}
        transform={`rotate(${calculateRotationForAway(adjustedAway)} ${center} ${center})`}
      />
      <rect
        x={19}
        y={-3}
        width={2}
        height={8}
        fill={normalizedAway !== 0 || (normalizedHome == 0 && normalizedAway == 0) ? (theme === T.DARK ? "#333" : "#fff") : "transparent"}
        transform={`rotate(${predictStatic(progressOffset2, calculateRotationForHome(adjustedHome))} ${center} ${center})`}
      />
      <rect
        x={19}
        y={-3}
        width={2}
        height={8}
        fill={normalizedHome !== 0 || (normalizedAway == 0 && normalizedHome == 0) ? (theme === T.DARK ? "#333" : "#fff") : "transparent"}
        transform={`rotate(${predictStatic2(progressOffset3, calculateRotationForAway(adjustedAway))} ${center} ${center})`}
      /> 
    </svg>
  );
};

export default Circle;

//TODO: 
//chekck negative values for rect
//add errors view 
